import { useEffect, useMemo, useState } from 'react';
import { useGuaranteedServiceList } from '../../api/guaranteedService.ts';
import { useTargetProbeLegs } from '../../api/useTargetProbes.ts';
import { useT, type I18nKey } from '../../i18n/index.ts';
import { Panel } from '../../ui/index.ts';
import { LegLampMark } from './LegLampMark.tsx';
import { LegRail } from './LegRail.tsx';
import { LegSkuDetail } from './LegSkuDetail.tsx';
import type { LegState } from './legPhysical.ts';
import { buildLegSkus, defaultSelectedSku, liveStates, skuMatchesQuery, type LegSku } from './legSkus.ts';

function toggleSet(current: ReadonlySet<string>, key: string): ReadonlySet<string> {
  const next = new Set(current);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

const LEGEND: readonly { state: LegState | 'dead'; key: I18nKey }[] = [
  { state: 'ok', key: 'guaranteed.legs.legend.ok' },
  { state: 'shadow', key: 'guaranteed.legs.legend.warn' },
  { state: 'tripped', key: 'guaranteed.legs.legend.bad' },
  { state: 'record', key: 'guaranteed.legs.legend.record' },
  { state: 'immune', key: 'guaranteed.legs.legend.immune' },
  { state: 'manual', key: 'guaranteed.legs.legend.manual' },
  { state: 'unprobed', key: 'guaranteed.legs.legend.unprobed' },
  { state: 'dead', key: 'guaranteed.legs.legend.dead' },
];

function Legend(): JSX.Element {
  const t = useT();
  return (
    <div
      className="flex flex-wrap items-center gap-x-3.5 gap-y-1 border-t border-line-soft px-4 py-2.5 text-micro text-paper-low"
      data-testid="guaranteed-leg-legend"
    >
      <span>{t('guaranteed.legs.legend.title')}</span>
      {LEGEND.map((item) => (
        <span key={item.state} className="inline-flex items-center gap-1.5">
          {item.state === 'dead'
            ? <LegLampMark state="ok" reachable={false} />
            : <LegLampMark state={item.state} />}
          {t(item.key)}
        </span>
      ))}
      <span>{t('guaranteed.legs.legend.regions')}</span>
      <span>{t('guaranteed.legs.legend.members')}</span>
    </div>
  );
}

/** Wall clock rounded to the minute: fresh enough for 「N 分钟前」, stable enough for `useMemo`. */
function useMinuteClock(fixed: number | undefined): number {
  const [now, setNow] = useState(() => fixed ?? Date.now());
  useEffect(() => {
    if (fixed !== undefined) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(timer);
  }, [fixed]);
  return fixed ?? now;
}

/**
 * 腿状态 (#2602, redesigned in #2658): a rail of downstream SKUs with one
 * lamp per physical leg, and the selected SKU's legs grouped by the role
 * they play in the routings actually in effect.
 */
export function LegStatusCard({ nowMs: fixedNowMs }: { readonly nowMs?: number }): JSX.Element {
  const t = useT();
  const nowMs = useMinuteClock(fixedNowMs);
  const legsQuery = useTargetProbeLegs();
  const list = useGuaranteedServiceList();
  const [query, setQuery] = useState('');
  const [abnormalOverride, setAbnormalOverride] = useState<boolean | null>(null);
  const [closed, setClosed] = useState<ReadonlySet<string>>(new Set());
  const [otherOpen, setOtherOpen] = useState(false);
  const [picked, setPicked] = useState<string | null>(null);
  const skus = useMemo(
    () => buildLegSkus(legsQuery.data?.legs ?? [], list.data?.sku ?? [], list.data?.org_sku ?? [], nowMs),
    [legsQuery.data, list.data, nowMs],
  );
  const configured = skus.filter((sku) => sku.configured);
  const others = skus.filter((sku) => !sku.configured);
  const abnormalOnly = abnormalOverride ?? configured.some((sku) => sku.abnormal);
  const show = (sku: LegSku): boolean => skuMatchesQuery(sku, query) && (!abnormalOnly || sku.abnormal);
  const shownConfigured = configured.filter(show);
  const shownOthers = others.filter(show);
  const selectedId = picked !== null && skus.some((sku) => sku.skuId === picked)
    ? picked
    : defaultSelectedSku([...shownConfigured, ...shownOthers], skus);
  const selected = skus.find((sku) => sku.skuId === selectedId);
  const liveLegs = liveStates(configured).length;
  const refreshed = legsQuery.dataUpdatedAt > 0
    ? Math.max(0, Math.floor((Date.now() - legsQuery.dataUpdatedAt) / 1000))
    : 0;

  return (
    <Panel
      testId="guaranteed-target-probe-card"
      caption={(
        <span className="inline-flex items-baseline gap-2">
          <span>{t('guaranteed.section.legs')}</span>
          <span className="text-label font-normal text-paper-low">
            {t('guaranteed.legs.scope', { skus: String(configured.length), legs: String(liveLegs) })}
          </span>
        </span>
      )}
      actions={(
        <span className="text-label text-paper-low">
          {t('guaranteed.leg.refreshed', { seconds: String(refreshed) })}
        </span>
      )}
    >
      {skus.length === 0 ? (
        <p className="m-0 px-3 py-3 text-dense text-paper-low">{t('guaranteed.leg.empty')}</p>
      ) : (
        <div className="flex items-start gap-3 p-3">
          <LegRail
            configured={configured}
            shownConfigured={shownConfigured}
            others={others}
            shownOthers={shownOthers}
            query={query}
            onQuery={setQuery}
            abnormalOnly={abnormalOnly}
            onAbnormalOnly={setAbnormalOverride}
            closed={closed}
            onToggle={(token) => setClosed((current) => toggleSet(current, token))}
            otherOpen={otherOpen}
            onOtherOpen={setOtherOpen}
            selected={selected?.skuId ?? null}
            onSelect={setPicked}
          />
          {selected === undefined ? (
            <p className="m-0 flex-1 px-3 py-3 text-dense text-paper-low">{t('guaranteed.legs.select')}</p>
          ) : (
            <LegSkuDetail key={selected.skuId} sku={selected} nowMs={nowMs} />
          )}
        </div>
      )}
      <Legend />
    </Panel>
  );
}
